import { CollaborationWorkList } from '~/data/collaboration';
import { Enum } from '~/data/enums';
import { browserLogError } from '~/utils/helper';

export const replaceTemplateIdInNotificationContent = (
  content: string,
  templates: CollaborationWorkList[],
  unknownTitle: string,
): string => {
  const notificationTemplateIdPattern =
    /(?:<strong>templateId_)(\d+)(?:<\/strong>)/g;

  return content.replace(notificationTemplateIdPattern, (match, c1) => {
    const templateId = parseInt(c1);

    const foundTemplate = templates.find(
      (t) => t.fCollaborationTemplateId === templateId,
    );
    const templateTitle = !!foundTemplate?.fCollaborationTemplateTitle
      ? foundTemplate?.fCollaborationTemplateTitle
      : unknownTitle;

    return match.replace(`templateId_${templateId}`, templateTitle);
  });
};

export const replaceTrainingCodeInNotificationContent = (
  content: string,
  internal: Enum[],
  external: Enum[],
): string => {
  const findEnumMatchInContent = (enums: Enum[]) =>
    enums.find((e) => content.includes(e.code));

  const foundEnum =
    findEnumMatchInContent(internal) ?? findEnumMatchInContent(external);

  return foundEnum ? content.replace(foundEnum.code, foundEnum.name) : content;
};

export const replaceTrainingCodeInTrainingExpirationNotificationContent = (
  content: string,
  internal: Enum[],
  external: Enum[],
): string => {
  const notificationTrainingTypePattern =
    /(?:<strong>(INTERNAL_|EXTERNAL_))(.+)(?:<\/strong>)/g;
  const notificationOtherTypePattern =
    /(?:<strong>(OTHER_))(.*?)(?:<\/strong>)/g;

  const otherMessage = content.match(notificationOtherTypePattern);

  if (otherMessage) {
    return content.replace(/(OTHER_)/g, '');
  }

  const internalOrExternalMessage = content.match(
    notificationTrainingTypePattern,
  );

  if (!!internalOrExternalMessage) {
    const contentHtmlString = internalOrExternalMessage[0];

    // Parse identifiers
    const [enumListType, ...codeFragments] = contentHtmlString
      .replace(/(<strong>|<\/strong>)/g, '')
      .split('_');
    const enumCode = codeFragments.join('_');

    const getEnumOptions = () => {
      switch (enumListType) {
        case 'INTERNAL':
          return internal;
        case 'EXTERNAL':
          return external;
        default:
          return [];
      }
    };

    const foundEnum = getEnumOptions().find((e) => e.code === enumCode);

    if (!foundEnum) {
      browserLogError(
        `Unable to find enum with code ${enumCode} in ${enumListType}_TRAINING_TYPE enums`,
      );
    }

    return content.replace(
      contentHtmlString,
      `<strong>${foundEnum?.name ?? enumCode}</strong>`,
    );
  }

  return content;
};

export enum TrainingStatusCodes {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  WAITING = 'WAITING',
  CANCELLED = 'CANCELLED',
  COMPLETED = 'COMPLETED',
  PARTICIPATED_PARTLY = 'PARTICIPATED_PARTLY',
  DID_NOT_SHOW_UP = 'DID_NOT_SHOW_UP',
  DID_NOT_COMPLETE = 'DID_NOT_COMPLETE',
  PENDING = 'PENDING',
  UNKNOWN = 'UNKNOWN',
}

export const replaceTrainingStatusCodeInNotificationsContent = (
  content: string,
  codes: TrainingStatusCodes[],
  translations: { [key in TrainingStatusCodes]: string },
) => {
  const foundCode = codes.find((c) => content.includes(c));
  const translation = !!foundCode
    ? translations[foundCode]
    : translations[TrainingStatusCodes.UNKNOWN];

  return !!foundCode ? content.replace(foundCode, translation) : content;
};
